import { Link, useOutletContext } from 'react-router-dom';
import type { WorkspaceViewId } from '../../components/CalculatorNavigation';
import { useSeo } from '../../hooks/useSeo';

export const financialRunwaySeo = {
  title: 'Financial Runway Calculator | FinBuffer',
  description: 'Calculate how many months your liquid savings could cover your monthly burn. Learn the formula, see an example, and try FinBuffer’s free calculator.',
  canonicalPath: '/learn/financial-runway',
} as const;

export default function WhatIsFinancialRunwayPage() {
  const { onSelectCalculator } = useOutletContext<{
    onSelectCalculator: (id: WorkspaceViewId) => void;
  }>();

  useSeo(financialRunwaySeo);

  return (
    <article className="seo-page">
      <header className="seo-page-header">
        <h1>Financial Runway Calculator</h1>
        <p className="seo-intro">
        Financial runway estimates how long your liquid savings could cover your monthly burn if your income stopped
        or changed. FinBuffer helps you explore that estimate using your own expenses, savings, and stable additional
        income—without sending your calculator inputs to a server.
        </p>
        <p className="seo-cta">
        <Link to="/" className="btn-primary" onClick={() => onSelectCalculator('runway')}>
          Open the Financial Runway Calculator
        </Link>
        </p>
      </header>

      <section>
        <h2>What Does Financial Runway Mean?</h2>
        <p>
          Your financial runway answers one calm, practical question: 
          <strong> "If my income stopped today, how long could I cover my essential expenses without new income?"</strong>
        </p>
        <p>
          The calculator's monthly burn starts with rent, living expenses, and loan payments. It subtracts additional
          income only when you mark that income as stable, and it never treats a negative burn as negative spending.
          This can help you explore a change in income or expenses, but it is an estimate based on the figures you enter.
        </p>
        <p>
          If you are specifically thinking about how much cash to hold, you may also find it useful to read the
          {' '}
          <a href="/learn/emergency-fund" className="seo-link">guide on how many months of emergency fund you may need</a>
          .
        </p>
      </section>

      <section>
        <h2>How to Calculate Your Financial Runway</h2>
        <h3>Step 1 – Estimate Your Monthly Burn</h3>
        <p>
          Enter the monthly costs you need to cover, such as rent, living expenses, and EMI payments. For example,
          imagine someone whose monthly costs total:
        </p>
        <ul>
          <li>Rent: 900</li>
          <li>Groceries and basics: 350</li>
          <li>Transport: 120</li>
          <li>Utilities and internet: 130</li>
          <li>Insurance: 100</li>
          <li>Loan EMIs and other fixed payments: 200</li>
        </ul>
        <p>
          The monthly costs total <strong>1,800</strong>. If you have additional income marked as stable, the
          calculator subtracts it when estimating monthly burn. In this example, assume there is no stable additional
          income, so monthly burn is also <strong>1,800</strong>.
        </p>

        <h3>Step 2 – Enter Your Liquid Savings</h3>
        <p>
          Enter cash and savings you consider readily accessible in the calculator's <strong>Liquid Savings</strong>
          field. Market-linked investments and EPF are entered separately and are excluded from runway, so do not add
          them to this amount.
        </p>
        <p>
          Continuing the example, imagine they have:
        </p>
        <ul>
          <li>Savings account: 3,500</li>
          <li>Short-term deposit: 1,900</li>
        </ul>
        <p>
          Their liquid savings would be <strong>5,400</strong>.
        </p>

        <h3>Step 3 – Use the Simple Runway Formula</h3>
        <p>
          You can now calculate your financial runway in months:
        </p>
        <p className="seo-formula">
          Financial runway (months) = Liquid savings ÷ Monthly burn
        </p>
        <p>
          Using the example numbers above:
        </p>
        <p>
          5,400 ÷ 1,800 = <strong>3 months of estimated financial runway</strong>.
        </p>
        <p>
          With these illustrative inputs, the estimate is three months. Actual expenses, income, and access to savings
          can change, so use the result as a planning aid rather than a prediction.
        </p>
      </section>

      <section>
        <h2>What Is a "Good" Financial Runway?</h2>
        <p>
          There is no perfect number that works for everyone, but many educators talk about broad ranges to describe
          how exposed or protected you might be:
        </p>
        <ul>
          <li>
            <strong>0–1 month:</strong> Very fragile. Even a short income gap could create stress, late payments, or
            reliance on debt.
          </li>
          <li>
            <strong>2–3 months:</strong> Basic safety buffer. You have some breathing room for short disruptions such as
            delayed salary or a short gap between jobs.
          </li>
          <li>
            <strong>4–6 months:</strong> Comfortable buffer. You can handle longer job searches or health breaks with
            less panic.
          </li>
          <li>
            <strong>6+ months:</strong> Strong resilience. You have time and space to make thoughtful decisions instead
            of rushed ones.
          </li>
        </ul>
        <p>
          Your ideal runway depends on income stability, family responsibilities, local job markets, and how much
          support you could receive in a crisis. Think of these ranges as gentle guidelines, not strict rules.
        </p>
      </section>

      <section>
        <h2>Examples: Different Runways in Practice</h2>
        <h3>Salaried Employee</h3>
        <p>
          Consider a salaried employee with take-home income of 2,500 per month and essential expenses of 1,700.
          They have 3,400 in accessible savings.
        </p>
        <p>
          Their runway is 3,400 ÷ 1,700 = <strong>2 months</strong>. A short job loss or medical leave would be
          stressful, but they have some time to adjust.
        </p>
        <h3>Freelancer With Variable Income</h3>
        <p>
          Now imagine a freelancer with average income of 3,200, essential expenses of 1,900, and savings of 7,600.
        </p>
        <p>
          Their runway is 7,600 ÷ 1,900 = <strong>4 months</strong>. Because their income is less predictable, this
          longer runway gives them room to handle slow periods with less pressure.
        </p>
      </section>

      <section>
        <h2>Why Financial Runway Matters for Peace of Mind</h2>
        <p>
          Knowing your runway is not about predicting disaster. It is about reducing anxiety around the question
          "What if something happens?". When you understand how long you could cope, you can:
        </p>
        <ul>
          <li>Make career decisions with more confidence</li>
          <li>Avoid high-interest debt during emergencies where possible</li>
          <li>Give yourself time to adjust your budget if your situation changes</li>
        </ul>
        <p>
          Even if your current runway is short, simply measuring it turns a vague worry into a clear, improvable number.
        </p>
      </section>

      <section>
        <h2>How to Improve Your Financial Runway</h2>
        <p>
          Improving your runway does not require dramatic changes. Small, steady steps can make a big difference over
          time:
        </p>
        <ul>
          <li>Reduce a few non-essential expenses and redirect the savings into a buffer</li>
          <li>Use part of any bonus or one-off income to grow your emergency fund</li>
          <li>Aim for your next small milestone: first 1 month, then 2, then 3</li>
        </ul>
        <p>
          Instead of aiming for a perfect number, focus on slowly moving from fragile towards safer.
        </p>
        <p>
          For a step-by-step plan to build that first cushion, you can follow the
          {' '}
          <a href="/learn/build-3-month-buffer" className="seo-link">3-month safety buffer guide</a>
          .
        </p>
      </section>

      <section>
        <h2>Check Your Own Financial Runway</h2>
        <p>
          You can work out your own runway with a pen and paper, or you can use a simple tool that does the maths for
          you. A good calculator will take in your monthly essentials, include your loan EMIs, and show you how many
          months of safety your current savings provide.
        </p>
        <p>
          This makes an abstract idea concrete and helps you decide which safety milestone to aim for next.
        </p>
        <p className="seo-cta">
          If you want to calculate your own financial runway, you can{' '}
          <Link to="/" className="seo-link" onClick={() => onSelectCalculator('runway')}>
            calculate your financial runway with the free FinBuffer calculator
          </Link>.
        </p>
      </section>

      <section>
        <h2>Frequently Asked Questions</h2>
        <h3>What is financial runway?</h3>
        <p>
          Financial runway is an estimate of how many months your liquid savings could cover your monthly burn if your
          income stopped or changed.
        </p>
        <h3>How do I calculate my financial runway?</h3>
        <p>
          Divide liquid savings by monthly burn. In FinBuffer, the burn estimate includes rent, living expenses, and
          loan payments, less additional income only when you mark it as stable.
        </p>
        <h3>What counts as liquid savings?</h3>
        <p>
          Use cash and savings you can access for near-term needs in the Liquid Savings field. FinBuffer excludes
          market-linked investments and EPF from runway; borrowed money such as credit-card limits is not savings.
        </p>
        <h3>How many months of expenses should I keep as an emergency fund?</h3>
        <p>
          There is no single target for everyone. Income stability, dependents, and essential costs all matter. See the
          <a href="/learn/emergency-fund" className="seo-link"> emergency fund guide</a> for factors to consider.
        </p>
        <h3>What happens when monthly expenses are zero?</h3>
        <p>
          If monthly burn is zero, the formula has no monthly amount to divide by and FinBuffer displays an infinite
          runway result. That is a mathematical result for the inputs—not a guarantee that expenses will stay at zero
          or that savings will never run out.
        </p>
      </section>

      <section>
        <h2>Explore Other FinBuffer Calculators</h2>
        <p>
          These tools estimate borrowing or savings scenarios with their own inputs; those estimates are separate
          from the financial runway formula. Each link opens its calculator in FinBuffer's shared workspace.
        </p>
        <ul>
          <li><Link to="/" className="seo-link" onClick={() => onSelectCalculator('emi')}>EMI calculator</Link></li>
          <li><Link to="/" className="seo-link" onClick={() => onSelectCalculator('sip')}>SIP calculator</Link></li>
          <li><Link to="/" className="seo-link" onClick={() => onSelectCalculator('lumpsum')}>Lump Sum calculator</Link></li>
          <li><Link to="/" className="seo-link" onClick={() => onSelectCalculator('fd')}>FD calculator</Link></li>
          <li><Link to="/" className="seo-link" onClick={() => onSelectCalculator('rd')}>RD calculator</Link></li>
        </ul>
        <p>
          For more context, read the <a href="/learn/safe-emi-percentage" className="seo-link">safe EMI guide</a> or
          the <a href="/learn/build-3-month-buffer" className="seo-link">3-month buffer guide</a>.
        </p>
      </section>
    </article>
  );
}
